import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { ANNEN_EIENDOM_ID } from '../components/eiendom/annenEiendom';
import { PageOrder } from '../components/page/pageOrder';
import { PageUrl } from '../components/page/pageUrl';
import { AppContext } from '../containers/appContextContainer';
import {
    DeleteSoeknad,
    NewSoeknadWeb,
    SendSoeknadWeb,
    SoeknadContainerWeb,
    SoeknadLaanContainerWeb,
    SoeknadLaanUpdateWeb,
    SoeknadStatusWeb,
    SoeknadstypeWeb,
} from '../generated-types';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';
import useFeatureToggle from './useFeatureToggle';

export const useSoeknadMedlaantaker = (soeknadtype: SoeknadstypeWeb) => {
    const { data: featureToggles } = useFeatureToggle();

    const { setError } = useContext(AppContext);
    const navigate = useNavigate();
    const { setPageOrder } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['soeknad', soeknadtype],
        queryFn: () => restClient.findSoeknad(soeknadtype),
        refetchOnMount: false,
        retry: 1,
    });

    useEffect(() => {
        if (query.data) {
            if (
                query.data.soeknad &&
                (query.data.soeknad.soeknadtype === SoeknadstypeWeb.FINANSIERINGSBEVIS ||
                    query.data.soeknad.soeknadtype === SoeknadstypeWeb.KJOEPE_BOLIG)
            ) {
                setPageOrder(PageOrder(SoeknadstypeWeb.MEDLAANTAKER_EGENKAPITAL, featureToggles?.utvideDatagrunnlag));
            }
            if (query.data.soeknad && query.data.soeknad.status !== SoeknadStatusWeb.STEG_MEDLAANTAKER_SAMTYKKER) {
                navigate(PageUrl(query.data.soeknad.status));
            }
            if (query.error) {
                setError({
                    errorType: errorTypes.FIND_SOEKNAD,
                    errorException: query.error as RequestExceptionWithResponse,
                });
            }
        }
    }, [query.data, query.error, navigate, setPageOrder, setError, featureToggles?.utvideDatagrunnlag]);

    return query;
};

export const useGetKjerneLaantaker = () => {
    return useMutation({
        mutationKey: ['laantaker'],
        mutationFn: (soeknadsId: string) => {
            return restClient.getLantaker(false, soeknadsId);
        },
        retry: 1,
    });
};

export const useBasicSoeknad = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const navigate = useNavigate();
    const query = useQuery({
        queryKey: ['soeknad', soeknadtype],
        queryFn: () => restClient.findBasicSoeknad(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.data && query.isFetchedAfterMount) {
            if (query.data.status !== SoeknadStatusWeb.NY_SOEKNAD && !query.data.status.includes('MEDLAANTAKER')) {
                navigate(PageUrl(query.data.status));
            }
        }
        if (query.error) {
            setError({
                errorType: errorTypes.FIND_BASIC_SOEKNAD,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.data, query.error, query.isFetchedAfterMount, navigate, setError]);

    return query;
};

export const useSoeknadOppsummering = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['oppsummering', soeknadtype],
        queryFn: () => restClient.getSoeknadOppsummering(soeknadtype),
        refetchOnMount: 'always',
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.FIND_SOEKNAD,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useSaveSoeknad = (successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['saveSoeknad'],
        mutationFn: (newSoeknad: NewSoeknadWeb) => restClient.saveSoeknad(newSoeknad),
        onSuccess: (data: NewSoeknadWeb) => {
            if (data.eiendom && data.eiendom.eiendomId.length === 0) {
                data.eiendom.eiendomId = ANNEN_EIENDOM_ID;
            }
            queryClient.setQueryData(['soeknad', data.soeknadtype], data);
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.SAVE_SOEKNAD, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useSendSoeknad = (successCallback: () => void) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['sendSoeknad'],
        mutationFn: (SendSoeknad: SendSoeknadWeb) => restClient.sendSoeknad(SendSoeknad),
        onSuccess: (ok: boolean) => {
            if (ok) {
                const soeknadContainer = queryClient.getQueryData<SoeknadContainerWeb>(['soeknad']);
                if (soeknadContainer?.soeknad?.status) {
                    soeknadContainer.soeknad.status = SoeknadStatusWeb.KLAR_FOR_OVERFOERING_KREDITTPORTALEN;
                }
                queryClient.setQueryData(['soeknad'], soeknadContainer);
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.SEND_SOEKNAD, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useDeleteSoeknad = (successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    return useMutation({
        mutationKey: ['deleteSoeknad'],
        mutationFn: (deleteSoeknad: DeleteSoeknad) => restClient.deleteSoeknad(deleteSoeknad),
        onSuccess: (data: boolean) => {
            if (data && successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_SOEKNAD, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useSoeknadLaan = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['soeknadLaan'],
        queryFn: () => restClient.getSoeknadLaan(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_SOEKNAD_LAAN,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useUpdateSoeknadLaan = (
    soeknadLaanContainer: SoeknadLaanContainerWeb | undefined,
    successCallback?: () => void,
) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateSoeknadLaan'],
        mutationFn: (soeknadLaanUpdate: SoeknadLaanUpdateWeb) => restClient.updateSoeknadLaan(soeknadLaanUpdate),
        onSuccess: (data: SoeknadLaanUpdateWeb) => {
            if (soeknadLaanContainer) {
                soeknadLaanContainer.laan.soektBeloep = data.soektBeloep;
                soeknadLaanContainer.laan.eksisterendeLaanebeloep = data.eksisterendeLaanebeloep;
                soeknadLaanContainer.laan.nedbetalingstid = data.nedbetalingstid;
                soeknadLaanContainer.oekeLaan = data.oekeLaan;
                soeknadLaanContainer.laaneformaal = data.laaneformaal;
                soeknadLaanContainer.annetLaaneformaal = data.annetLaaneformaal;
                queryClient.setQueryData(['soeknadLaan'], soeknadLaanContainer);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({
                errorType: errorTypes.UPDATE_SOEKNAD_LAAN,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export default useSoeknadMedlaantaker;
